// ─────────────────────────────────────────────────────────────────────────────
//  LookupField — a real dropdown that also accepts typing
//
//  Used for Category / Core Package on New Diagnosis and Procedure Group on
//  New Procedure. Options are the DISTINCT values already in the tariff tables.
//
//  Behaviour:
//    * click the arrow (or the field) and the full list drops down
//    * type to filter that list
//    * type something not in the list and it is saved as a new value
//
//  Two details that matter and were wrong the first time:
//
//    1. forcePopupIcon. MUI hides the dropdown arrow when freeSolo is on,
//       because it assumes a free-text box. That left this looking like a
//       plain text field with no affordance to open anything. The arrow is
//       now always rendered, and openOnFocus means one click shows everything.
//
//    2. An empty list must not look the same as a working one. Previously a
//       failed lookup was swallowed and the field just said "Type a value",
//       which is indistinguishable from "the database really has none". Now a
//       failed load says so, in red, so the cause is obvious.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { Autocomplete, TextField, CircularProgress, Typography, Box } from '@mui/material';

const LookupField = ({
  label,
  value,
  onChange,              // (nextString) => void
  options = [],
  loading = false,
  loadError = '',        // set when the lookup request itself failed
  error = false,
  helperText = '',
  placeholder = '',
  sx = {},
}) => {
  const count = options.length;

  // Priority: the caller's validation error, then a load failure, then status.
  const statusText = loading
    ? 'Loading existing values…'
    : loadError
      ? `Could not load the list: ${loadError}`
      : count
        ? `${count} existing value${count === 1 ? '' : 's'} — click the arrow to browse, or type a new one`
        : 'No existing values found in the database yet — type one to create it';

  return (
    <Autocomplete
      freeSolo
      forcePopupIcon            // always show the arrow, even with freeSolo
      openOnFocus               // one click opens the full list
      autoSelect
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      size="small"
      options={options}
      loading={loading}
      value={value || ''}
      onChange={(_e, next) => onChange(typeof next === 'string' ? next : (next ?? ''))}
      onInputChange={(_e, next, reason) => {
        if (reason === 'input') onChange(next);
      }}
      // Show everything when the box is empty; filter as soon as they type.
      filterOptions={(opts, state) => {
        const q = (state.inputValue || '').trim().toLowerCase();
        if (!q) return opts;
        const starts = [], contains = [];
        for (const o of opts) {
          const s = String(o).toLowerCase();
          if (s.startsWith(q)) starts.push(o);
          else if (s.includes(q)) contains.push(o);
        }
        return [...starts, ...contains];
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ fontSize: '0.82rem' }}>
          {option}
        </Box>
      )}
      ListboxProps={{ style: { maxHeight: 260, fontSize: '0.82rem' } }}
      noOptionsText={
        <Typography sx={{ fontSize: '0.78rem' }}>
          {count === 0
            ? 'Nothing in the database yet — what you type will be saved'
            : 'No match — what you type will be saved as a new value'}
        </Typography>
      }
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error || !!loadError}
          placeholder={placeholder}
          helperText={helperText || statusText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={15} sx={{ mr: 0.5 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default LookupField;
